/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import React, { type JSX, useCallback, ReactElement, useMemo, forwardRef, Ref, RefAttributes } from "react";

import Select, { SelectProps as MuiSelectProps, SelectChangeEvent } from "@mui/material/Select";
import { SxProps, Theme } from "@mui/material/styles";
import classnames from "classnames";
import { FieldValues, useController } from "react-hook-form";

import { getLabel, isInputRequired } from "../../../common/helpers/validators";
import { useCurrentInputVibe } from "../../../common/hooks/useCurrentInputVibe";
import { useCustomError } from "../../../common/hooks/useCustomError";
import { useCustomInputProps } from "../../../common/hooks/useCustomInputProps";
import { DashboardBaseInput, getSizeStyles } from "../BaseInput";
import { DashboardInputLabel } from "../InputLabel/DashboardInputLabel";

import { disabledAutoFocusMenuProps } from "./constants";
import { BaseSelectProps, FormSelectProps, SelectProps } from "./types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useValidators } from "@/common/hooks/useValidators";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { ChevronDown, Cross } from "@/icons/outlined";

export const DashboardSelect = forwardRef(function SelectInner<T extends FieldValues>(
  { control, rules, name, ...rest }: SelectProps<T>,
  ref: Ref<HTMLSelectElement>
) {
  return control && name ? (
    <FormSelect {...rest} ref={ref} control={control} name={name} rules={rules} />
  ) : (
    <BaseSelect {...rest} name={name} ref={ref} />
  );
}) as <T extends FieldValues>(props: SelectProps<T> & RefAttributes<HTMLSelectElement>) => JSX.Element;

function FormSelectInner<T extends FieldValues>(
  { control, defaultValue, label, name, onBlur, onChange, onClear, rules, noAsterisk, ...rest }: FormSelectProps<T>,
  ref: Ref<HTMLSelectElement>
) {
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);

  const { field } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as never,
    rules: validators,
  });

  const handleChange: BaseSelectProps["onChange"] = useCallback(
    (event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
      onChange?.(event, child);
      field.onChange(event.target.value);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    field.onBlur();
  }, [field, onBlur]);

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onClear?.(event);
      field.onChange("");
    },
    [field, onClear]
  );

  return (
    <BaseSelect
      {...rest}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      onClear={onClear ? handleClear : undefined}
      value={field.value}
      label={getLabel(isRequired, label, noAsterisk)}
      name={name}
    />
  );
}

FormSelectInner.displayName = "FormSelect";

const FormSelect = forwardRef(FormSelectInner) as unknown as <T extends FieldValues>(
  props: FormSelectProps<T> & RefAttributes<HTMLSelectElement>
) => JSX.Element;

const BaseSelect = forwardRef<HTMLSelectElement, BaseSelectProps>(
  (
    {
      children,
      className,
      vibe,
      "data-test": dataTest,
      disableAutoFocus,
      disabled,
      disableAutocomplete,
      error,
      errorMessage,
      headerRight,
      helperText,
      inputProps,
      label,
      MenuProps,
      size,
      tooltipText,
      name,
      sx,
      slotProps,
      onClear,
      value,
      ...rest
    },
    ref
  ): ReactElement<BaseSelectProps> => {
    const currenVibe = useCurrentInputVibe(vibe);

    const currentError = useCustomError(error, errorMessage);
    const customInputProps = useCustomInputProps({ inputProps, disableAutocomplete });

    const mergedMenuProps = useMemo(
      () => ({
        ...MenuProps,
        ...(disableAutoFocus ? disabledAutoFocusMenuProps : undefined),
      }),
      [MenuProps, disableAutoFocus]
    );

    const { inputLabel: inputLabelSlotProps, icon: iconSlotProps, ...restOfSlotProps } = slotProps || {};

    const renderBaseSelectIcon = useCallback(
      (props: { className?: string; open?: boolean }) => {
        const hasValue = value !== undefined && value !== "";

        // Show clear icon if value exists and onClear is provided
        // Note: We don't pass props.className here because MUI's .MuiSelect-icon class has pointer-events: none
        if (hasValue && onClear) {
          return (
            <DashboardIcon
              inline
              size="extraSmall"
              vibe="inherit"
              className={slotProps?.icon?.className}
              data-test={`${dataTest}-clear-button`}
              sx={mergeSx(
                (theme) => ({
                  color: "unset",
                  cursor: "pointer",
                  position: "absolute",
                  right: theme.spacing(1),
                  top: "unset",
                  zIndex: 1,
                  ...(currenVibe === "form"
                    ? {
                        fill: theme.palette.text.form.moderate,
                        color: theme.palette.text.form.moderate,
                      }
                    : {}),
                }),
                iconSlotProps?.sx
              )}
              onClick={(e) => {
                e.stopPropagation();
                onClear(e);
              }}
            >
              <Cross />
            </DashboardIcon>
          );
        }

        return (
          <DashboardIcon
            inline
            size="extraSmall"
            vibe="inherit"
            className={classnames(props.className, slotProps?.icon?.className)}
            data-test={`${dataTest}-button`}
            {...iconSlotProps}
            sx={mergeSx(
              (theme) => ({
                color: "unset",
                cursor: "pointer",
                position: "absolute",
                right: theme.spacing(1),
                top: "unset",
                transition: "transform 0.2s",
                ...(props.open ? { transform: "rotate(180deg)" } : {}),
                ...(currenVibe === "form"
                  ? {
                      fill: theme.palette.text.form.moderate,
                      color: theme.palette.text.form.moderate,
                    }
                  : {}),
              }),
              iconSlotProps?.sx
            )}
          >
            <ChevronDown />
          </DashboardIcon>
        );
      },
      [currenVibe, dataTest, iconSlotProps, slotProps?.icon?.className, value, onClear]
    );

    const composedSelectSx: SxProps<Theme> = mergeSx(
      (theme: Theme) => ({
        // Necessary for css specificity
        [`&&.MuiInputBase-root`]: {
          position: "relative",
          "&& .MuiSelect-select": {
            borderRadius: theme.radius(1),
            "&:hover, &:focus, &:focus-visible, &:focus-within": {
              backgroundColor: "unset",
            },
            ...getSizeStyles(size === "small" ? "small" : "medium", theme),
            paddingRight: `calc(${theme.spacing(3)} + ${theme.icon.size.default.small}px)`,
          },
          "&& .MuiSelect-icon": {
            color: "unset",
            cursor: "pointer",
            position: "absolute",
            right: theme.spacing(1),
            top: "unset",
            transition: "transform 0.2s",
            ...(currenVibe === "form"
              ? {
                  fill: theme.palette.text.form.moderate,
                  color: theme.palette.text.form.moderate,
                }
              : {}),
          },
          "&& .MuiSelect-iconOpen": {
            transform: "rotate(180deg)",
          },
        },
      }),
      sx
    ) as SxProps<Theme>;

    // Necessary to override the root slot props to add the data-test attribute
    const selectSlotProps = {
      root: {
        "data-test": dataTest,
        ...restOfSlotProps?.root,
      },
    } as MuiSelectProps["slotProps"];

    return (
      <DashboardInputLabel
        className={className}
        vibe={currenVibe}
        errorMessage={errorMessage}
        headerRight={headerRight}
        helperText={helperText}
        label={label}
        tooltipText={tooltipText}
        data-test={`${dataTest}-label`}
        {...inputLabelSlotProps}
      >
        <Select
          variant="standard"
          {...rest}
          slotProps={selectSlotProps}
          value={value ?? ""}
          displayEmpty
          MenuProps={mergedMenuProps}
          ref={ref}
          sx={composedSelectSx}
          disabled={disabled}
          IconComponent={renderBaseSelectIcon}
          inputProps={customInputProps}
          input={
            <DashboardBaseInput vibe={currenVibe} disabled={disabled} error={currentError} size={size} noPadding />
          }
          name={name}
        >
          {children}
        </Select>
      </DashboardInputLabel>
    );
  }
);

BaseSelect.displayName = "Select";
